import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react'
import { Search, ChevronLeft, ChevronRight } from 'lucide-react'
import { toast } from 'react-toastify'
import serviceMasterService from '../../services/serviceMasterService'
import './ServiceSelectionTable.css'

const serviceCodeOf = (service) => String(service?.serviceCode || service?.itemCode || '')

const sortByServiceCode = (rows) => [...rows]
  .sort((a, b) => serviceCodeOf(a).localeCompare(serviceCodeOf(b), undefined, { numeric: true, sensitivity: 'base' }))
  .map((service, index) => ({ ...service, serialNumber: index + 1 }))

const invoiceLinesKey = (rows) => (rows || [])
  .map((service) => [
    service.serviceCode || service.itemCode || service.serviceId || service._id || '',
    service.quantity || service.qty || 0,
    service.unitRate || service.rate || 0,
    service.additionalInfo || '',
    service.SACCode || service.selectSAC || ''
  ].join(':'))
  .sort()
  .join('|')

const ServiceSelectionTable = forwardRef(({ onServiceDataChange, initialServiceData = [], isEditMode = false, isViewMode = false, isApproveMode = false }, ref) => {
  const [serviceData, setServiceData] = useState([])
  
  const [allServiceData, setAllServiceData] = useState([]) // Store all services from master
  const [filledServices, setFilledServices] = useState([]) // Store filled services (with qty and rate)
  const [previewServices, setPreviewServices] = useState([]) // Store services that were shown in preview
  const [invoiceServices, setInvoiceServices] = useState([]) // Store invoice services (for edit mode Esc functionality)
  const [isPreviewMode, setIsPreviewMode] = useState(false)
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const itemsPerPage = 10
  
  // Use ref to access current serviceData without causing dependency issues
  const serviceDataRef = useRef(serviceData)
  useEffect(() => {
    serviceDataRef.current = serviceData
  }, [serviceData])

  // Track if we've initialized with filled services to prevent reset
  const initializedRef = useRef(false)
  const appliedLinesKeyRef = useRef(null)

  // In edit/view/approve mode, only show invoice services (initialServiceData)
  // In create mode, load from service master
  const isReadOnlyMode = isViewMode || isApproveMode;
  const isEditOrApprove = isEditMode || isApproveMode;

  // Function to merge initial service data with master data
  const mergeInitialServiceData = async () => {
    setLoading(true);
    try {
      const response = await serviceMasterService.getAllServices({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        status: 'Yes',
        sortBy: 'serviceCode',
        sortOrder: 'asc',
      });
      
      if (response && response.success) {
        const activeServices = response.data.filter(service => service.active === 'Yes');
        
        // Create a map of initial service data by service ID for quick lookup
        const initialServiceMap = new Map();
        initialServiceData.forEach(service => {
          const serviceId = service._id || service.serviceId || service.service?._id || service.service;
          if (serviceId) {
            initialServiceMap.set(serviceId.toString(), service);
          }
        });
        
        // Transform master services and merge with initial data
        const transformedData = activeServices.map((service, index) => {
          const serviceId = service._id || service.id;
          const initialService = initialServiceMap.get(serviceId?.toString());
          
          if (initialService && (initialService.quantity > 0 || initialService.unitRate > 0 || initialService.lineValue > 0)) {
            // Use initial service data (preserve filled values)
            return {
              ...service,
              serialNumber: index + 1,
              additionalInfo: initialService.additionalInfo || '',
              quantity: initialService.quantity || initialService.qty || 0,
              unitRate: initialService.unitRate || initialService.rate || 0,
              lineValue: initialService.lineValue || (initialService.quantity * initialService.unitRate) || 0.00,
              selectSAC: initialService.selectSAC || initialService.SACCode || service.selectSAC || '',
              SACCode: initialService.SACCode || initialService.selectSAC || service.SACCode || '',
              gstRate: initialService.gstRate || service.gstRate || 0,
              cgstRate: initialService.cgstRate || service.cgstRate || 0,
              sgstRate: initialService.sgstRate || service.sgstRate || 0,
              igstRate: initialService.igstRate || service.igstRate || 0
            };
          }
          
          // New service from master (not in initial data)
          return {
            ...service,
            serialNumber: index + 1,
            additionalInfo: '',
            quantity: 0,
            unitRate: 0,
            lineValue: 0.00
          };
        });
        
        const orderedServices = sortByServiceCode(transformedData);
        setServiceData(orderedServices);
        setAllServiceData(orderedServices);
        setTotalPages(response.pagination?.totalPages || 1);
        setTotalRecords(activeServices.length);
        
        // Notify parent of the merged data
        if (onServiceDataChange) {
          onServiceDataChange(orderedServices);
        }
      }
    } catch (error) {
      console.error('Error merging service data:', error);
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  // Initialize with filled services from initialServiceData on first mount
  useEffect(() => {
    if (!isEditOrApprove && !isViewMode && !initializedRef.current) {
      // Check if initialServiceData has any filled services (with qty or rate > 0)
      const hasFilledServices = initialServiceData.some(service => 
        (service.quantity > 0 || service.unitRate > 0 || service.lineValue > 0)
      );
      
      if (hasFilledServices && serviceData.length === 0) {
        // Transform initial service data to preserve filled values
        const transformedData = initialServiceData.map((service, index) => ({
          ...service,
          _id: service._id || service.serviceId || service.service?._id || service.service,
          serviceId: service.serviceId || service.service?._id || service.service || service._id,
          serviceCode: service.serviceCode || service.itemCode || '',
          serviceDescription: service.serviceDescription || service.itemName || service.description || '',
          selectSAC: service.selectSAC || service.SACCode || '',
          SACCode: service.SACCode || service.selectSAC || '',
          serialNumber: index + 1,
          additionalInfo: service.additionalInfo || '',
          quantity: service.quantity || service.qty || 0,
          unitRate: service.unitRate || service.rate || 0,
          lineValue: service.lineValue || (service.quantity * service.unitRate) || 0.00
        }));
        
        const orderedServices = sortByServiceCode(transformedData);
        setServiceData(orderedServices);
        setAllServiceData(orderedServices);
        initializedRef.current = true;
        
        // Notify parent
        if (onServiceDataChange) {
          onServiceDataChange(orderedServices);
        }
        return; // Don't load from master if we have filled services
      }
    }
  }, [initialServiceData, isEditOrApprove, isViewMode]);

  // Load active services data (only in create mode and if not initialized with filled services)
  useEffect(() => {
    if (!isEditOrApprove && !isViewMode && !initializedRef.current) {
      loadServiceData();
    }
  }, [currentPage, searchTerm, isEditOrApprove, isViewMode])

  // In edit/view/approve mode, initialize with invoice services only.
  // The parent echoes these rows back as initialServiceData, so skip a repeat of the same lines.
  useEffect(() => {
    if (isEditOrApprove || isViewMode) {
      const linesKey = invoiceLinesKey(initialServiceData)
      if (linesKey === appliedLinesKeyRef.current) return
      appliedLinesKeyRef.current = linesKey

      if (initialServiceData && initialServiceData.length > 0) {
        // Transform invoice services to table format with proper serial numbers starting from 1
        const transformedServices = initialServiceData.map((service, index) => ({
          ...service,
          _id: service._id || service.serviceId || service.service?._id || service.service,
          serviceId: service.serviceId || service.service?._id || service.service || service._id,
          serviceCode: service.serviceCode || service.itemCode || '',
          serviceDescription: service.serviceDescription || service.itemName || service.description || '',
          selectSAC: service.selectSAC || service.SACCode || '',
          SACCode: service.SACCode || service.selectSAC || '',
          gstRate: service.gstRate || 0,
          cgstRate: service.cgstRate || 0,
          sgstRate: service.sgstRate || 0,
          igstRate: service.igstRate || 0,
          serialNumber: index + 1, // Start from 1
          additionalInfo: service.additionalInfo || '',
          quantity: service.quantity || service.qty || 0,
          unitRate: service.unitRate || service.rate || 0,
          lineValue: service.lineValue || (service.quantity * service.unitRate) || 0
        }));
        
        const orderedServices = sortByServiceCode(transformedServices);
        setServiceData(orderedServices);
        setInvoiceServices(orderedServices); // Store for Esc functionality in edit mode
        setAllServiceData(orderedServices);
        setTotalPages(1);
        setTotalRecords(orderedServices.length);
        
        // Notify parent of the data
        if (onServiceDataChange) {
          onServiceDataChange(orderedServices);
        }
      } else {
        setServiceData([]);
        setTotalRecords(0);
      }
    }
  }, [initialServiceData, isEditOrApprove, isViewMode])

  // Merge initial service data when both are available (for create mode)
  useEffect(() => {
    if (!isEditOrApprove && !isViewMode && initialServiceData.length > 0) {
      setServiceData(prevServiceData => {
        if (prevServiceData.length === 0) return prevServiceData;
        
        const mergedData = prevServiceData.map(service => {
          const initialService = initialServiceData.find(init => (init._id || init.serviceId) === service._id);
          if (initialService && (initialService.quantity > 0 || initialService.unitRate > 0)) {
            return {
              ...service,
              additionalInfo: initialService.additionalInfo || service.additionalInfo || '',
              quantity: initialService.quantity || 0,
              unitRate: initialService.unitRate || 0,
              lineValue: initialService.lineValue || 0.00
            };
          }
          return service;
        });
        
        return mergedData;
      });
    }
  }, [initialServiceData, isEditOrApprove, isViewMode])

  const loadServiceData = async () => {
    setLoading(true)
    try {
      const response = await serviceMasterService.getAllServices({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        status: 'Yes', // Filter for active services (backend uses 'status' parameter)
        sortBy: 'serviceCode',
        sortOrder: 'asc',
      })
      
      if (response && response.success) {
        // Filter for active services only (client-side backup)
        const activeServices = response.data.filter(service => service.active === 'Yes')
        
        // Transform the data to include editable fields
        // Serial number should start from 1, not based on pagination
        const transformedData = activeServices.map((service, index) => ({
          ...service,
          serialNumber: index + 1, // Start from 1, not based on pagination
          additionalInfo: '',
          quantity: 0,
          unitRate: 0,
          lineValue: 0.00 // Initialize as number with 2 decimal places
        }))
        
        const orderedServices = sortByServiceCode(transformedData)
        setServiceData(orderedServices)
        setAllServiceData(orderedServices) // Store all services
        setTotalPages(response.pagination?.totalPages || 1)
        setTotalRecords(activeServices.length) // Use filtered count
      } else {
        console.error('Service data error:', response)
        toast.error(response?.message || 'Failed to fetch service master records')
      }
    } catch (error) {
      console.error('Error loading service master data:', error)
      
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
        return
      }
      
      if (error.response?.status === 403) {
        toast.error('You do not have permission to access service master data.')
      } else if (error.response?.status === 404) {
        toast.error('Service master service not found.')
      } else {
        toast.error('Failed to load service master data. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e) => {
    e.preventDefault()
    setCurrentPage(1)
    loadServiceData()
  }

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage)
    }
  }

  const handleInputChange = (serviceId, field, value) => {
    setServiceData(prevData => {
      const updatedData = prevData.map(service => {
        if (service._id === serviceId) {
          const updatedService = { ...service, [field]: value }
          
          // Calculate line value when quantity or unit rate changes
          if (field === 'quantity' || field === 'unitRate') {
            const qty = field === 'quantity' ? parseFloat(value) || 0 : parseFloat(service.quantity) || 0
            const rate = field === 'unitRate' ? parseFloat(value) || 0 : parseFloat(service.unitRate) || 0
            const lineValue = qty * rate
            updatedService.lineValue = Math.round(lineValue * 100) / 100 // Round to 2 decimal places
            
            // Track filled services (have both qty and rate > 0)
            const finalQty = field === 'quantity' ? parseFloat(value) || 0 : parseFloat(updatedService.quantity) || 0
            const finalRate = field === 'unitRate' ? parseFloat(value) || 0 : parseFloat(updatedService.unitRate) || 0
            
            setFilledServices(prev => {
              const filtered = prev.filter(s => (s._id || s.id) !== serviceId)
              if (finalQty > 0 && finalRate > 0) {
                return [...filtered, updatedService]
              }
              return filtered
            })
          }
          
          return updatedService
        }
        return service
      })
      
      // Update allServiceData as well
      setAllServiceData(prevAllData => {
        return prevAllData.map(service => {
          if (service._id === serviceId) {
            return updatedData.find(s => s._id === serviceId) || service
          }
          return service
        })
      })
      
      // Notify parent component of data changes
      if (onServiceDataChange) {
        onServiceDataChange(updatedData)
      }
      
      return updatedData
    })
  }

  // Handle Preview - show only filled services
  const handlePreview = React.useCallback(() => {
    // Get filled services from current serviceData
    const filled = serviceData.filter(service => {
      const qty = parseFloat(service.quantity) || 0
      const rate = parseFloat(service.unitRate) || 0
      return qty > 0 && rate > 0
    })
    
    if (filled.length === 0) {
      toast.info('No services with quantity and rate entered')
      return
    }
    
    // Store the preview services so we can preserve them when Esc is clicked
    const orderedFilled = sortByServiceCode(filled)
    setPreviewServices(orderedFilled)
    setServiceData(orderedFilled)
    setIsPreviewMode(true)
    if (onServiceDataChange) {
      onServiceDataChange(orderedFilled)
    }
    setCurrentPage(1)
    setTotalPages(1)
    setTotalRecords(filled.length)
    toast.success(`Showing ${filled.length} service(s) with quantity and rate entered. You can now create the invoice.`)
  }, [serviceData, onServiceDataChange])

  // Handle Esc - show preview services + all other services from master
  // In edit mode: show invoice services + remaining services from master
  // In create mode: show preview services + remaining services from master
  const handleEsc = React.useCallback(async () => {
    // In edit mode, preserve invoice services and load remaining ones
    if (isEditMode) {
      // Get current invoice services - prioritize invoiceServices state, fallback to current serviceData
      // In edit mode, invoiceServices should contain the original invoice services
      let currentInvoiceServices = invoiceServices.length > 0 ? [...invoiceServices] : []
      
      // If invoiceServices is empty, try to get from current serviceData using ref
      // This handles the case where invoiceServices wasn't set properly
      if (currentInvoiceServices.length === 0) {
        const currentServiceData = serviceDataRef.current
        if (currentServiceData.length > 0) {
          // In edit mode, all services in serviceData should be invoice services
          currentInvoiceServices = currentServiceData.filter(s => {
            // Services with quantity or rate > 0 are invoice services
            const qty = parseFloat(s.quantity) || 0
            const rate = parseFloat(s.unitRate) || 0
            return qty > 0 || rate > 0
          })
          
          // If still empty, use all current serviceData (they're all invoice services in edit mode)
          if (currentInvoiceServices.length === 0) {
            currentInvoiceServices = [...currentServiceData]
          }
        }
      }
      
      if (currentInvoiceServices.length === 0) {
        toast.info('No invoice services found. Loading all services from master.')
      }
      
      // Create a set of invoice service IDs for comparison
      // Try multiple ID fields to ensure we catch all variations
      const invoiceServiceIds = new Set()
      const invoiceServiceCodes = new Set() // Also track by service code as fallback
      
      currentInvoiceServices.forEach(service => {
        const serviceId = service._id || service.serviceId || service.id || service.service?._id || service.service
        const serviceCode = service.serviceCode || service.itemCode || ''
        
        if (serviceId) {
          invoiceServiceIds.add(String(serviceId))
        }
        if (serviceCode) {
          invoiceServiceCodes.add(String(serviceCode))
        }
      })
      
      // Reload all services from master
      setLoading(true)
      try {
        const response = await serviceMasterService.getAllServices({
          page: 1,
          limit: 10000, // Get all services
          search: searchTerm,
          status: 'Yes',
          sortBy: 'serviceCode',
          sortOrder: 'asc'
        })
        
        if (response && response.success) {
          const activeServices = response.data.filter(service => service.active === 'Yes')
          
          // Get services that are NOT in invoice (to be loaded fresh)
          // Check both ID and service code to ensure we don't miss any
          const otherServices = activeServices
            .filter(service => {
              const serviceId = String(service._id || service.id || '')
              const serviceCode = String(service.serviceCode || '')
              
              // Check if service is in invoice by ID or code
              const isInInvoiceById = invoiceServiceIds.has(serviceId)
              const isInInvoiceByCode = invoiceServiceCodes.has(serviceCode)
              const isInInvoice = isInInvoiceById || isInInvoiceByCode
              
              return !isInInvoice
            })
            .map((service) => ({
              ...service,
              serialNumber: 0, // Will be set below
              additionalInfo: '',
              quantity: 0,
              unitRate: 0,
              lineValue: 0.00
            }))
          
          // Combine invoice services (with their preserved data) + other services
          // Serial numbers: invoice services start from 1, other services continue
          const combinedData = [
            ...currentInvoiceServices.map((service, index) => ({
              ...service,
              serialNumber: index + 1
            })),
            ...otherServices.map((service, index) => ({
              ...service,
              serialNumber: currentInvoiceServices.length + index + 1
            }))
          ]
          
          const orderedServices = sortByServiceCode(combinedData)
          setServiceData(orderedServices)
          setAllServiceData(orderedServices)
          // Update invoiceServices to current invoice services for future Esc clicks
          setInvoiceServices(currentInvoiceServices)
          setIsPreviewMode(false)
          setCurrentPage(1)
          const totalServices = combinedData.length
          setTotalPages(Math.ceil(totalServices / itemsPerPage))
          setTotalRecords(totalServices)
          toast.info(`Showing ${currentInvoiceServices.length} invoice service(s) + ${otherServices.length} other service(s) available for selection.`)
        } else {
          toast.error('Failed to load services from master')
        }
      } catch (error) {
        console.error('Error reloading services:', error)
        toast.error('Failed to reload services')
      } finally {
        setLoading(false)
      }
      return
    }
    
    // Original logic for create mode (preview services + remaining)
    const previewServiceIds = new Set()
    previewServices.forEach(service => {
      previewServiceIds.add(service._id || service.id)
    })
    
    // Reload all services from master
    setLoading(true)
    try {
      const response = await serviceMasterService.getAllServices({
        page: 1,
        limit: 10000, // Get all services
        search: searchTerm,
        status: 'Yes',
        sortBy: 'serviceCode',
        sortOrder: 'asc',
      })
      
      if (response && response.success) {
        const activeServices = response.data.filter(service => service.active === 'Yes')
        
        // Get services that are NOT in preview (to be loaded fresh)
        const otherServices = activeServices
          .filter(service => !previewServiceIds.has(service._id || service.id))
          .map((service) => ({
            ...service,
            additionalInfo: '',
            quantity: 0,
            unitRate: 0,
            lineValue: 0.00
          }))
        
        // Combine preview services (with their preserved data) + other services
        const combinedData = [
          ...previewServices.map((service, index) => ({
            ...service,
            serialNumber: index + 1
          })),
          ...otherServices.map((service, index) => ({
            ...service,
            serialNumber: previewServices.length + index + 1
          }))
        ]
        
        const orderedServices = sortByServiceCode(combinedData)
        setServiceData(orderedServices)
        setAllServiceData(orderedServices)
        setIsPreviewMode(false) // Reset preview mode, which will disable Create Invoice button
        setCurrentPage(1)
        const totalServices = combinedData.length
        setTotalPages(Math.ceil(totalServices / itemsPerPage))
        setTotalRecords(totalServices)
        toast.info(`Showing ${previewServices.length} preview service(s) + ${otherServices.length} other service(s) available for selection. Click Preview after entering quantity and rate to enable Create Invoice.`)
      }
    } catch (error) {
      console.error('Error reloading services:', error)
      toast.error('Failed to reload services')
    } finally {
      setLoading(false)
    }
  }, [previewServices, searchTerm, isEditMode, invoiceServices])

  // Check if invoice can be created (preview clicked and has valid services)
  const canCreateInvoice = React.useMemo(() => {
    if (!isPreviewMode) return false
    const filled = serviceData.filter(service => {
      const qty = parseFloat(service.quantity) || 0
      const rate = parseFloat(service.unitRate) || 0
      return qty > 0 && rate > 0
    })
    return filled.length > 0
  }, [isPreviewMode, serviceData])

  // Expose methods and state to parent via ref
  const getBillableLines = () => (serviceDataRef.current || []).filter((service) => {
    const qty = parseFloat(service.quantity ?? service.qty) || 0
    const rate = parseFloat(service.unitRate ?? service.rate) || 0
    return qty > 0 && rate > 0
  })

  useImperativeHandle(ref, () => ({
    handlePreview,
    handleEsc,
    canCreateInvoice,
    isPreviewMode,
    getBillableLines
  }), [handlePreview, handleEsc, canCreateInvoice, isPreviewMode])

  if (loading) {
    return (
      <div className="service-table-loading">
        <div className="service-loading-spinner">Loading services...</div>
      </div>
    )
  }

  return (
    <div className="service-selection-table">
      {/* Search and Pagination - Hide in edit/view/approve mode */}
      {!isEditOrApprove && !isViewMode && (
        <div className="service-table-controls">
          <form onSubmit={handleSearch} className="service-search-form">
            <div className="service-search-wrapper">
              <Search size={16} className="service-search-icon" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="service-search-input"
                placeholder="Search services..."
              />
            </div>
          </form>
          
          <div className="service-pagination">
            <button
              className="service-pagination-btn"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={16} />
            </button>
            <span className="service-pagination-info">
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="service-pagination-btn"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Service Table */}
      <div className="service-table-container">
        {serviceData.length === 0 ? (
          <div className="service-empty-state">
            <div className="empty-icon">🔧</div>
            <h3>No services found</h3>
            <p>No active service master records available.</p>
          </div>
        ) : (
          <table className="service-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left', width: '40px' }}>#</th>
                <th style={{ textAlign: 'left', width: '100px' }}>Service Code</th>
                <th style={{ textAlign: 'left', width: '200px' }}>Service Description</th>
                <th style={{ textAlign: 'left', width: '80px' }}>SAC Code</th>
                <th style={{ textAlign: 'left', width: '60px' }}>GST %</th>
                <th style={{ textAlign: 'left', width: '150px' }}>Additional Information</th>
                <th style={{ textAlign: 'center', width: '80px' }}>Qty.</th>
                <th style={{ textAlign: 'center', width: '100px' }}>Unit Rate</th>
                <th style={{ textAlign: 'right', width: '100px' }}>Line Value</th>
              </tr>
            </thead>
            <tbody>
              {serviceData.map((service) => (
                <tr key={service._id}>
                  <td className="service-serial-cell" style={{ textAlign: 'left' }}>{service.serialNumber}</td>
                  <td className="service-code-cell" style={{ textAlign: 'left' }}>{service.serviceCode}</td>
                  <td className="service-description-cell" style={{ textAlign: 'left' }}>{service.serviceDescription}</td>
                  <td className="service-sac-cell" style={{ textAlign: 'left' }}>{service.selectSAC}</td>
                  <td className="service-gst-cell" style={{ textAlign: 'left' }}>{service.gstRate}%</td>
                  <td className="service-additional-cell" style={{ textAlign: 'left' }}>
                    <input
                      type="text"
                      value={service.additionalInfo}
                      onChange={(e) => handleInputChange(service._id, 'additionalInfo', e.target.value)}
                      className="service-input"
                      placeholder="Additional info"
                      disabled={isReadOnlyMode}
                    />
                  </td>
                  <td className="service-qty-cell" style={{ textAlign: 'center' }}>
                    <input
                      type="number"
                      value={service.quantity}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty string, positive numbers, and 0
                        if (value === '' || (!isNaN(value) && parseFloat(value) >= 0)) {
                          handleInputChange(service._id, 'quantity', value);
                        }
                      }}
                      className="service-input service-number-input"
                      placeholder="0"
                      min="0"
                      step="1"
                      disabled={isReadOnlyMode}
                    />
                  </td>
                  <td className="service-rate-cell" style={{ textAlign: 'center' }}>
                    <input
                      type="number"
                      value={service.unitRate}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty string, positive numbers, and 0
                        if (value === '' || (!isNaN(value) && parseFloat(value) >= 0)) {
                          handleInputChange(service._id, 'unitRate', value);
                        }
                      }}
                      className="service-input service-number-input"
                      placeholder="0"
                      min="0"
                      step="0.01"
                      disabled={isReadOnlyMode}
                    />
                  </td>
                  <td className="service-line-value-cell" style={{ textAlign: 'right' }}>
                    {typeof service.lineValue === 'number' ? service.lineValue.toFixed(2) : service.lineValue}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
})

ServiceSelectionTable.displayName = 'ServiceSelectionTable'

export default ServiceSelectionTable
